"use client";

import { ChartConfig } from "@/components/ui/Chart";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useMenuItemsContext } from "@/context/MenuItemsContext";
import {
  ClientEstate,
  BankStatementImportActivityInput,
  EstateStats,
  EstateStatsMap,
  EstateWatchDogOffer,
  FormDataMyEstate,
  TaxReport,
  Transaction,
  TransactionPeriodVerification,
  TransactionPreset,
} from "@/types";
import {
  addNewMyEstate,
  deleteMyEstate,
  fetchEstateReport,
  fetchEstateAnalysis,
  fetchEstateWatchDogResults,
  saveTransactions,
  sendTransactionReminder as sendTransactionReminderRequest,
  verifyTransactions as verifyTransactionsRequest,
} from "@/utils";
import { OTHER_ITEMS } from "@/utils/HelperConstants";
import { notifyUserActivityUpdated } from "@/hooks/useUserActivity";
import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  applyTransactionAmount,
  buildAuthPayload,
  expandRecurringTransactions,
  mapOfferToChartItem,
  median,
  normalizeAccessories,
} from "@/utils/MyEstateHelper";

type EstateContextType = {
  isGraphView: boolean;
  isPredictionView: boolean;
  setIsGraphView: React.Dispatch<React.SetStateAction<boolean>>;
  setIsPredictionView: React.Dispatch<React.SetStateAction<boolean>>;
  isTaxView: boolean;
  setIsTaxView: React.Dispatch<React.SetStateAction<boolean>>;
  chartData: { date: string; price: number; offer: TaxReport }[];
  taxReport: TaxReport[];
  allEstateList: ClientEstate[];
  isLoadingEstate: boolean;
  selectedEstate: ClientEstate | null;
  setSelectedEstate: (estate: ClientEstate | null) => void;
  selectedYear: number;
  selectedMonth: number;
  setSelectedYear: React.Dispatch<React.SetStateAction<number>>;
  setSelectedMonth: React.Dispatch<React.SetStateAction<number>>;
  minYear: number;
  maxYear: number;
  lastUpdatedRecurring: Date | null;
  lastUpdatedOneTime: Date | null;
  activeChart: keyof typeof chartConfig;
  setActiveChart: React.Dispatch<SetStateAction<keyof typeof chartConfig>>;
  chartConfig: typeof chartConfig;
  total: { avgPrice: number; median: number; totalEstates: number };
  analysis: {
    avgPriceBefore: number;
    avgPriceAfter: number;
    avgPriceSell: number;
  };
  portfolioEstimatedValue: number | null;
  transactions: Transaction[];
  yearTransactions: Transaction[];
  monthTransactions: Transaction[];
  income: number;
  expense: number;
  profit: number;
  totalsUpToMonth: {
    income: number;
    expense: number;
    profit: number;
  };
  lastYearProfit: number;
  totalProfit: number;
  currentYear: number;
  currentMonth: number;
  formDataMyEstate: FormDataMyEstate;
  defaultEstate: FormDataMyEstate;
  setFormDataMyEstate: Dispatch<SetStateAction<FormDataMyEstate>>;
  handleDiscardChanges: () => void;
  resetDetailSearch: () => void;
  saveMyEstate: () => Promise<void>;
  openEditModalTransactions: (
    preset?: TransactionPreset | TransactionPreset[],
  ) => void;
  openEditModal: () => void;
  isLoadingAnalysis: boolean;
  childrenEstateOffers: EstateWatchDogOffer[];
  childChartDataBeforeReco: {
    date: string;
    price: number;
    offer: EstateWatchDogOffer;
  }[];
  childChartDataAfterReco: {
    date: string;
    price: number;
    offer: EstateWatchDogOffer;
  }[];
  childChartDataSellPrice: {
    date: string;
    price: number;
    offer: EstateWatchDogOffer;
  }[];
  deleteEstate: () => Promise<void>;
  defaultTransaction: Transaction;
  transactionPreset: TransactionPreset | TransactionPreset[] | null;
  handleSubmitTransaction: (
    transactionsForm: Transaction[],
    doShowSuccessMessage: boolean,
  ) => Promise<void>;
  verifyTransactionPeriods: (
    verifications: TransactionPeriodVerification[],
    importActivity?: BankStatementImportActivityInput,
  ) => Promise<boolean>;
  sendTransactionReminder: (
    transactionId: number,
    period: string,
  ) => Promise<boolean>;
  estateStats: Record<string, EstateStats>;
};

const SOFTIQ_PRIMARY = "#1B4242";

const chartConfig = {
  views: {
    label: "Cena",
  },
  avgPrice: {
    label: "Průměrná cena",
    color: SOFTIQ_PRIMARY,
  },
  median: {
    label: "Medián",
  },
  totalProfit: {
    label: "Zisk",
  },
  avgPriceBefore: {
    label: "v aktuálním stavu",
    color: SOFTIQ_PRIMARY,
  },
  avgPriceAfter: {
    label: "po kompletní rekonstrukci",
    color: SOFTIQ_PRIMARY,
  },
} satisfies ChartConfig;

const INITIAL_CURRENT_YEAR = 2026;
const INITIAL_CURRENT_MONTH = 1;
const INITIAL_TODAY = new Date(
  INITIAL_CURRENT_YEAR,
  INITIAL_CURRENT_MONTH - 1,
  1,
);

const defaultContext: EstateContextType = {
  isGraphView: false,
  isPredictionView: false,
  setIsGraphView: () => {},
  setIsPredictionView: () => {},
  isTaxView: false,
  setIsTaxView: () => {},
  chartData: [],
  taxReport: [],
  allEstateList: [],
  isLoadingEstate: true,
  selectedEstate: null,
  setSelectedEstate: () => {},
  selectedYear: INITIAL_CURRENT_YEAR,
  selectedMonth: INITIAL_CURRENT_MONTH,
  setSelectedYear: () => {},
  setSelectedMonth: () => {},
  minYear: 2026,
  maxYear: INITIAL_CURRENT_YEAR,
  lastUpdatedRecurring: null,
  lastUpdatedOneTime: null,
  activeChart: "avgPrice",
  setActiveChart: () => {},
  chartConfig,
  total: { avgPrice: 0, median: 0, totalEstates: 0 },
  analysis: { avgPriceBefore: 0, avgPriceAfter: 0, avgPriceSell: 0 },
  portfolioEstimatedValue: null,
  transactions: [],
  yearTransactions: [],
  monthTransactions: [],
  income: 0,
  expense: 0,
  profit: 0,
  totalsUpToMonth: {
    income: 0,
    expense: 0,
    profit: 0,
  },
  lastYearProfit: 0,
  totalProfit: 0,
  currentYear: INITIAL_CURRENT_YEAR,
  currentMonth: INITIAL_CURRENT_MONTH,
  formDataMyEstate: {
    name: "",
    id: 0,
    isNew: true,
    estateType: "",
    flatCategory: "",
    exactAddress: "",
    address: "",
    dateFrom: "",
    dateTo: "",
    ownerShip: "",
    estateCondition: "",
    accessories: [],
    usableArea: null,
    purchasePrice: null,
    note: "",
    tenantName: "",
    tenantPhone: "",
    tenantEmail: "",
    tenantNote: "",
    managerName: "",
    managerPhone: "",
    managerEmail: "",
    managerNote: "",
    doResetWatchDog: false,
  } as FormDataMyEstate,
  defaultEstate: {
    name: "",
    id: 0,
    isNew: true,
    estateType: "",
    flatCategory: "",
    exactAddress: "",
    address: "",
    dateFrom: "",
    dateTo: "",
    ownerShip: "",
    estateCondition: "",
    accessories: [],
    usableArea: null,
    purchasePrice: null,
    note: "",
    tenantName: "",
    tenantPhone: "",
    tenantEmail: "",
    tenantNote: "",
    managerName: "",
    managerPhone: "",
    managerEmail: "",
    managerNote: "",
    doResetWatchDog: false,
  } as FormDataMyEstate,
  setFormDataMyEstate: () => {},
  handleDiscardChanges: () => {},
  resetDetailSearch: () => {},
  saveMyEstate: async () => {},
  openEditModalTransactions: () => {},
  openEditModal: () => {},
  isLoadingAnalysis: true,
  childrenEstateOffers: [],
  childChartDataBeforeReco: [],
  childChartDataAfterReco: [],
  childChartDataSellPrice: [],
  deleteEstate: async () => {},
  defaultTransaction: {
    id: null,
    originalId: 0,
    name: "",
    amount: 0,
    transactionType: "INCOME",
    recurrence: "MONTHLY",
    dateFrom: "",
    originalDateFrom: "",
    dateTo: null,
    updatedAt: "",
    isPrediction: false,
    taxDeductible: false,
    showInDashboard: true,
  },
  transactionPreset: null,
  handleSubmitTransaction: async () => {},
  verifyTransactionPeriods: async () => false,
  sendTransactionReminder: async () => false,
  estateStats: {},
};

const EstateContext = createContext<EstateContextType>(defaultContext);

export const EstateProvider = ({ children }: { children: ReactNode }) => {
  const { language } = useLanguage();
  const copy = {
    cs: {
      price: "Cena",
      averagePrice: "Průměrná cena",
      median: "Medián",
      profit: "Zisk",
      currentState: "v aktuálním stavu",
      renovated: "po kompletní rekonstrukci",
    },
    en: {
      price: "Price",
      averagePrice: "Average price",
      median: "Median",
      profit: "Profit",
      currentState: "in current condition",
      renovated: "after complete renovation",
    },
  }[language];
  const localizedChartConfig = useMemo(
    () =>
      ({
        views: {
          label: copy.price,
        },
        avgPrice: {
          label: copy.averagePrice,
          color: SOFTIQ_PRIMARY,
        },
        median: {
          label: copy.median,
        },
        totalProfit: {
          label: copy.profit,
        },
        avgPriceBefore: {
          label: copy.currentState,
          color: SOFTIQ_PRIMARY,
        },
        avgPriceAfter: {
          label: copy.renovated,
          color: SOFTIQ_PRIMARY,
        },
      }) satisfies typeof chartConfig,
    [
      copy.averagePrice,
      copy.currentState,
      copy.median,
      copy.price,
      copy.profit,
      copy.renovated,
    ],
  );
  const [isGraphView, setIsGraphView] = useState(defaultContext.isGraphView);
  const [isPredictionView, setIsPredictionView] = useState(
    defaultContext.isPredictionView,
  );
  const [isTaxView, setIsTaxView] = useState(defaultContext.isTaxView);
  const defaultEstate = defaultContext.defaultEstate;
  const [chartData, setChartData] = useState<
    { date: string; price: number; offer: TaxReport }[]
  >([]);
  const [isLoadingEstate, setIsLoadingEstate] = useState(
    defaultContext.isLoadingEstate,
  );
  const [taxReport, setTaxReport] = useState<TaxReport[]>([]);
  const [selectedYear, setSelectedYear] = useState(defaultContext.selectedYear);
  const [selectedMonth, setSelectedMonth] = useState(
    defaultContext.selectedMonth,
  );
  const [allEstateList, setAllEstateList] = useState<ClientEstate[]>([]);
  const [selectedEstate, setSelectedEstate] = useState<ClientEstate | null>(
    null,
  );
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(
    defaultContext.isLoadingAnalysis,
  );
  const [childrenEstateOffers, setChildrenEstateOffers] = useState<
    EstateWatchDogOffer[]
  >([]);
  const [aggregateAnalysis, setAggregateAnalysis] = useState<
    EstateContextType["analysis"] | null
  >(null);
  const [portfolioEstimatedValue, setPortfolioEstimatedValue] = useState<
    number | null
  >(null);
  const estateWatchDogRequestRef = React.useRef(0);
  const [minYear, setMinYear] = useState(defaultContext.minYear);
  const [maxYear, setMaxYear] = useState(defaultContext.maxYear);
  const [lastUpdatedRecurring, setLastUpdatedRecurring] = useState(
    defaultContext.lastUpdatedRecurring,
  );
  const [lastUpdatedOneTime, setLastUpdatedOneTime] = useState(
    defaultContext.lastUpdatedOneTime,
  );
  const [activeChart, setActiveChart] =
    React.useState<keyof typeof chartConfig>("avgPrice");
  const total = { avgPrice: 5, median: 5, totalEstates: 1 };
  const { isOpen, setIsOpen, stepNumber, setStepNumber, setSelectedItem } =
    useMenuItemsContext();
  const { userInfoGlobal, setErrorMessage } = useAppContext();
  const [formDataMyEstate, setFormDataMyEstate] = useState<FormDataMyEstate>(
    defaultContext.formDataMyEstate,
  );
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionPreset, setTransactionPreset] = useState<
    TransactionPreset | TransactionPreset[] | null
  >(null);
  const [today, setToday] = useState(INITIAL_TODAY);
  const [currentYear, setCurrentYear] = useState(INITIAL_CURRENT_YEAR);
  const [currentMonth, setCurrentMonth] = useState(INITIAL_CURRENT_MONTH);
  const defaultTransaction = defaultContext.defaultTransaction;

  useEffect(() => {
    const now = new Date();
    const nextCurrentYear = now.getFullYear();
    const nextCurrentMonth = now.getMonth() + 1;

    setToday(now);
    setCurrentYear(nextCurrentYear);
    setCurrentMonth(nextCurrentMonth);
    setSelectedYear(nextCurrentYear);
    setSelectedMonth(nextCurrentMonth);
    setMaxYear(nextCurrentYear);
  }, []);

  const handleDiscardChanges = () => {
    setIsOpen(false);
    setFormDataMyEstate(defaultContext.formDataMyEstate);
  };

  async function saveMyEstate() {
    setIsOpen(false);
    setStepNumber(1);
    const isNewEstate = !formDataMyEstate.id || formDataMyEstate.id === 0;
    const estateIdsBeforeSave = new Set(
      allEstateList.map((estate) => estate.id),
    );
    const wasSaved = await saveMyEstateInfo({
      ...formDataMyEstate,
      isNew: isNewEstate,
    });
    if (!wasSaved) return;

    const refreshedEstates = await getClientEstates();
    const savedEstate = isNewEstate
      ? refreshedEstates.find((estate) => !estateIdsBeforeSave.has(estate.id))
      : refreshedEstates.find((estate) => estate.id === formDataMyEstate.id);

    if (savedEstate) {
      setSelectedEstate(savedEstate);
    }
  }

  const saveMyEstateInfo = async (formDataMyEstate: FormDataMyEstate) => {
    if (!userInfoGlobal?.token) return false;

    try {
      const payload = buildAuthPayload(
        userInfoGlobal!.email,
        userInfoGlobal!.token,
        formDataMyEstate,
      );
      const response = await addNewMyEstate(payload, userInfoGlobal!.token);
      return (
        response.status >= 200 &&
        response.status < 300 &&
        response.data?.success !== false
      );
    } catch (err) {
      setErrorMessage(String(err));
      return false;
    } finally {
      handleDiscardChanges();
    }
  };

  function resetDetailSearch() {
    if (stepNumber === 2) {
      setFormDataMyEstate({
        ...formDataMyEstate,
        tenantName: "",
        tenantPhone: "",
        tenantEmail: "",
        tenantNote: "",
      });
    } else if (stepNumber === 3) {
      setFormDataMyEstate({
        ...formDataMyEstate,
        managerName: "",
        managerPhone: "",
        managerEmail: "",
        managerNote: "",
      });
    }
    setStepNumber(1);
  }

  const getClientEstates = async () => {
    let allEstates: ClientEstate[] = [];
    try {
      if (!userInfoGlobal?.token) return allEstates;
      setIsLoadingEstate(true);

      const payload = buildAuthPayload(
        userInfoGlobal.email,
        userInfoGlobal.token,
      );

      const response = await fetchEstateReport(payload, userInfoGlobal.token);
      if (response.status === 200) {
        setTaxReport(response.data.data);
        allEstates = response.data.data
          .flatMap((tr: TaxReport) => tr.estate)
          .map((estate: ClientEstate) => ({
            ...estate,
            accessories: normalizeAccessories(estate.accessories),
          }));
        setAllEstateList(allEstates);
      }
    } catch (err) {
      setErrorMessage(String(err));
    } finally {
      setIsLoadingEstate(false);
    }
    return allEstates;
  };

  const verifyTransactionPeriods = async (
    verifications: TransactionPeriodVerification[],
    importActivity?: BankStatementImportActivityInput,
  ) => {
    if (
      !userInfoGlobal?.token ||
      !selectedEstate ||
      verifications.length === 0
    ) {
      return false;
    }

    try {
      const payload = buildAuthPayload(
        userInfoGlobal.email,
        userInfoGlobal.token,
        {
          estateId: selectedEstate.id,
          verifications,
          ...(importActivity ? { importActivity } : {}),
        },
      );
      const response = await verifyTransactionsRequest(
        payload,
        userInfoGlobal.token,
      );
      const wasVerified =
        response.status >= 200 &&
        response.status < 300 &&
        response.data?.success !== false;
      if (wasVerified) {
        if (importActivity) notifyUserActivityUpdated();
        await getClientEstates();
      }
      return wasVerified;
    } catch (err) {
      setErrorMessage(String(err));
      return false;
    }
  };

  const sendTransactionReminder = async (
    transactionId: number,
    period: string,
  ) => {
    if (!userInfoGlobal?.token || !selectedEstate || !transactionId) {
      return false;
    }

    try {
      const payload = buildAuthPayload(
        userInfoGlobal.email,
        userInfoGlobal.token,
        {
          estateId: selectedEstate.id,
          transactionId,
          period,
        },
      );
      const response = await sendTransactionReminderRequest(
        payload,
        userInfoGlobal.token,
      );
      const wasSent =
        response.status >= 200 &&
        response.status < 300 &&
        response.data?.success !== false;
      if (wasSent) notifyUserActivityUpdated();
      return wasSent;
    } catch (err) {
      setErrorMessage(String(err));
      return false;
    }
  };

  useEffect(() => {
    if (userInfoGlobal?.token) {
      getClientEstates();
    }
  }, [userInfoGlobal]);

  useEffect(() => {
    if (allEstateList && allEstateList.length > 0) {
      if (!selectedEstate) {
        setSelectedEstate(allEstateList[0]);
      } else {
        const stillInList = allEstateList.find(
          (e) => e.id === selectedEstate.id,
        );
        if (stillInList) {
          setSelectedEstate(stillInList);
        } else {
          setSelectedEstate(allEstateList[0]);
        }
      }
    } else {
      setSelectedEstate(null);
    }
  }, [allEstateList]);

  useEffect(() => {
    let isCancelled = false;

    if (
      allEstateList.length === 0 ||
      !userInfoGlobal?.email ||
      !userInfoGlobal.token
    ) {
      setPortfolioEstimatedValue(null);
      return;
    }

    setPortfolioEstimatedValue(null);

    void Promise.all(
      allEstateList.map(async (estate) => {
        const fallbackValue = estate.purchasePrice ?? 0;

        try {
          const payload = buildAuthPayload(
            userInfoGlobal.email,
            userInfoGlobal.token,
            { realiqId: estate.id },
          );
          const response = await fetchEstateAnalysis(
            payload,
            userInfoGlobal.token,
          );

          if (response.status !== 200) return fallbackValue;

          const estimatedValue = response.data?.data?.avgPriceSell ?? 0;

          return estimatedValue > 0 ? estimatedValue : fallbackValue;
        } catch {
          return fallbackValue;
        }
      }),
    ).then((estateValues) => {
      if (!isCancelled) {
        setPortfolioEstimatedValue(
          estateValues.reduce((total, value) => total + value, 0),
        );
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [allEstateList, userInfoGlobal?.email, userInfoGlobal?.token]);

  useEffect(() => {
    if (selectedEstate != null) {
      setTransactions(selectedEstate.transactions);
      const transactionYears = (selectedEstate.transactions ?? [])
        .map((transaction) => new Date(transaction.dateFrom).getFullYear())
        .filter((year) => Number.isFinite(year));
      const estateStartYear = new Date(selectedEstate.dateFrom).getFullYear();
      setMinYear(Math.min(estateStartYear, ...transactionYears));
      setMaxYear(Math.max(currentYear, ...transactionYears));
    }
  }, [currentYear, selectedEstate]);

  // Preprocess transactions
  useEffect(() => {
    if (!selectedEstate) return;

    const expanded = expandRecurringTransactions(
      selectedEstate.transactions,
      selectedYear,
      today,
    );

    setTransactions(expanded);
  }, [selectedEstate, selectedYear]);

  const yearTransactions = React.useMemo(
    () =>
      transactions.filter(
        (t) => new Date(t.dateFrom).getFullYear() === selectedYear,
      ),
    [transactions, selectedYear],
  );

  const monthTransactions = React.useMemo(
    () =>
      yearTransactions.filter(
        (t) => new Date(t.dateFrom).getMonth() === selectedMonth - 1,
      ),
    [transactions, selectedYear, selectedMonth],
  );

  const { income, expense, profit } = React.useMemo(() => {
    let income = 0;
    let expense = 0;

    yearTransactions
      .filter((t) => t.showInDashboard)
      .forEach((t) => {
        if (t.transactionType === "INCOME") income += t.amount;
        else expense += t.amount;
      });

    return { income, expense, profit: income - expense };
  }, [yearTransactions]);

  const lastYearProfit = React.useMemo(() => {
    if (!selectedEstate) return 0;

    const lastYear = selectedYear - 1;
    const today = new Date();
    const lastYearExpanded = expandRecurringTransactions(
      selectedEstate.transactions,
      lastYear,
      today,
    );

    let profitValue = 0;
    lastYearExpanded
      .filter((t) => t.showInDashboard)
      .filter((t) => new Date(t.dateFrom).getFullYear() === lastYear)
      .forEach((t) => {
        if (t.transactionType === "INCOME") profitValue += t.amount;
        else profitValue -= t.amount;
      });

    return profitValue;
  }, [selectedEstate, selectedYear]);

  const totalsUpToMonth = React.useMemo(() => {
    if (selectedYear !== currentYear) {
      return { income, expense, profit };
    }

    let inc = 0;
    let exp = 0;

    transactions
      .filter((t) => new Date(t.dateFrom).getFullYear() === selectedYear)
      .forEach((t) => {
        const txMonth = new Date(t.dateFrom).getMonth() + 1;
        if (txMonth <= currentMonth) {
          if (t.transactionType === "INCOME") inc += t.amount;
          else exp += t.amount;
        }
      });

    return { income: inc, expense: exp, profit: inc - exp };
  }, [transactions, selectedYear, income, expense, profit]);

  const totalProfit = React.useMemo(() => {
    if (!selectedEstate) return 0;

    return selectedEstate.transactions.reduce((profit, t) => {
      const start = new Date(t.dateFrom);
      const end = t.dateTo ? new Date(t.dateTo) : today;
      const effectiveEnd = end < today ? end : today;

      let transactionProfit = 0;

      switch (t.recurrence) {
        case "MONTHLY": {
          let current = new Date(start);
          while (current <= effectiveEnd) {
            transactionProfit = applyTransactionAmount(
              transactionProfit,
              t.transactionType,
              t.amount,
            );
            current.setMonth(current.getMonth() + 1);
          }
          break;
        }
        case "YEARLY": {
          let current = new Date(start);
          while (current <= effectiveEnd) {
            transactionProfit = applyTransactionAmount(
              transactionProfit,
              t.transactionType,
              t.amount,
            );
            current.setFullYear(current.getFullYear() + 1);
          }
          break;
        }
        case "ONCE": {
          if (start <= today) {
            transactionProfit = applyTransactionAmount(
              transactionProfit,
              t.transactionType,
              t.amount,
            );
          }
          break;
        }
      }

      return profit + transactionProfit;
    }, 0);
  }, [selectedEstate]);

  const openEditModalTransactions = (
    preset?: TransactionPreset | TransactionPreset[],
  ) => {
    const presets = Array.isArray(preset) ? preset : preset ? [preset] : [];
    const presetDate = presets.find((item) => item.dateFrom)?.dateFrom;
    const openingDate = presetDate ? new Date(presetDate) : new Date();
    if (!Number.isNaN(openingDate.getTime())) {
      setSelectedYear(openingDate.getFullYear());
      setSelectedMonth(openingDate.getMonth() + 1);
    }
    setTransactionPreset(preset ?? null);
    setIsOpen(true);
    setSelectedItem(OTHER_ITEMS[1]);
  };

  const openEditModal = () => {
    setIsOpen(true);
    if (selectedEstate) {
      setFormDataMyEstate({
        ...selectedEstate,
        accessories: normalizeAccessories(selectedEstate.accessories),
        isNew: false,
      });
    }
    setSelectedItem(OTHER_ITEMS[2]);
  };

  // CHILD DATA
  useEffect(() => {
    if (!selectedEstate) {
      estateWatchDogRequestRef.current += 1;
      setChildrenEstateOffers([]);
      setAggregateAnalysis(null);
      setIsLoadingAnalysis(false);
      return;
    }

    getChildrenResults();
  }, [selectedEstate]);

  const getChildrenResults = async () => {
    const requestId = ++estateWatchDogRequestRef.current;
    try {
      if (!userInfoGlobal?.token || !selectedEstate) return;
      setIsLoadingAnalysis(true);
      setChildrenEstateOffers([]);
      setAggregateAnalysis(null);

      const payload = buildAuthPayload(
        userInfoGlobal.email,
        userInfoGlobal.token,
        { realiqId: selectedEstate.id },
      );

      const [offersResult, analysisResult] = await Promise.allSettled([
        fetchEstateWatchDogResults(payload, userInfoGlobal.token),
        fetchEstateAnalysis(payload, userInfoGlobal.token),
      ]);
      if (requestId !== estateWatchDogRequestRef.current) return;
      if (offersResult.status === "fulfilled" && offersResult.value.status === 200) {
        setChildrenEstateOffers(offersResult.value.data.data ?? []);
      }
      if (
        analysisResult.status === "fulfilled" &&
        analysisResult.value.status === 200 &&
        analysisResult.value.data?.success !== false
      ) {
        setAggregateAnalysis(analysisResult.value.data.data ?? null);
      }
    } catch (err) {
      if (requestId === estateWatchDogRequestRef.current) {
        setErrorMessage(String(err));
      }
    } finally {
      if (requestId === estateWatchDogRequestRef.current) {
        setIsLoadingAnalysis(false);
      }
    }
  };

  const {
    analysis,
    childChartDataBeforeReco,
    childChartDataAfterReco,
    childChartDataSellPrice,
  } = React.useMemo(() => {
    const groupA: {
      date: string;
      price: number;
      offer: EstateWatchDogOffer;
    }[] = [];
    const groupB: {
      date: string;
      price: number;
      offer: EstateWatchDogOffer;
    }[] = [];
    const groupC: {
      date: string;
      price: number;
      offer: EstateWatchDogOffer;
    }[] = [];

    childrenEstateOffers.forEach((item) => {
      const dataItem = mapOfferToChartItem(item);
      if (!dataItem) return;

      if (item.analysisType === "RENT_CURRENT") groupA.push(dataItem);
      else if (item.analysisType === "RENT_RENOVATED") groupB.push(dataItem);
      else if (item.analysisType === "SALE") groupC.push(dataItem);
    });

    const medianA = median(groupA.map((i) => i.price));
    const medianB = median(groupB.map((i) => i.price));
    const medianC = median(groupC.map((i) => i.price));
    return {
      analysis: aggregateAnalysis ?? {
        avgPriceBefore: medianA,
        avgPriceAfter: medianB > medianA ? medianB : medianA,
        avgPriceSell: medianC,
      },
      childChartDataBeforeReco: groupA,
      childChartDataAfterReco: groupB,
      childChartDataSellPrice: groupC,
    };
  }, [childrenEstateOffers, aggregateAnalysis]);

  const deleteEstate = async () => {
    try {
      if (!userInfoGlobal?.token || !selectedEstate) return;

      const payload = buildAuthPayload(
        userInfoGlobal.email,
        userInfoGlobal.token,
        {
          realiqId: selectedEstate.id,
        },
      );

      await deleteMyEstate(payload, userInfoGlobal.token);
    } catch (err) {
      setErrorMessage(String(err));
    } finally {
      await getClientEstates();
    }
  };

  const handleSubmitTransaction = async (
    transactionsForm: Transaction[],
    _doShowSuccessMessage: boolean,
  ) => {
    try {
      if (!userInfoGlobal?.token || !selectedEstate) return;

      const payload = buildAuthPayload(
        userInfoGlobal.email,
        userInfoGlobal.token,
        {
          transactions: transactionsForm.map((t) => ({
            ...t,
            id: t.originalId,
            estateId: selectedEstate.id,
            amount: Number(t.amount),
          })),
        },
      );

      const response = await saveTransactions(payload, userInfoGlobal.token);
      if (response.status !== 200) return;
      if (
        transactionsForm.some((transaction) => transaction.bankImportFileName)
      ) {
        notifyUserActivityUpdated();
      }
    } catch (err) {
      setErrorMessage(String(err));
    } finally {
      setIsOpen(false);
      await getClientEstates();
    }
  };

  const estateStats = useMemo<EstateStatsMap>(() => {
    const stats: EstateStatsMap = {};
    const currentMonth = isPredictionView ? 12 : today.getMonth() + 1;

    allEstateList.forEach((estate) => {
      let currentIncome = 0;
      let currentExpense = 0;
      let lastYearProfit = 0;

      let profitTotal = 0; // full-year prediction

      const currentYearExpanded = expandRecurringTransactions(
        estate.transactions,
        currentYear,
        today,
      );

      currentYearExpanded.forEach((t) => {
        const txMonth = new Date(t.dateFrom).getMonth() + 1;

        const value = t.transactionType === "INCOME" ? t.amount : -t.amount;

        // YTD values (changes with prediction view)
        if (txMonth <= currentMonth) {
          if (t.transactionType === "INCOME") currentIncome += t.amount;
          else currentExpense += t.amount;
        }

        // Full year prediction (always all months)
        profitTotal += value;
      });

      const lastYearExpanded = expandRecurringTransactions(
        estate.transactions,
        currentYear - 1,
        today,
      );

      lastYearExpanded.forEach((t) => {
        if (t.transactionType === "INCOME") lastYearProfit += t.amount;
        else lastYearProfit -= t.amount;
      });

      stats[estate.id] = {
        currentYearStats: {
          income: currentIncome,
          expense: currentExpense,
          profit: currentIncome - currentExpense,
          profitTotal: profitTotal,
        },
        lastYearStats: {
          profit: lastYearProfit,
        },
      };
    });

    return stats;
  }, [allEstateList, currentYear, today, isPredictionView]);

  useEffect(() => {
    if (isGraphView) {
      setIsTaxView(true);
      setIsPredictionView(true);
    }
  }, [isGraphView]);

  return (
    <EstateContext.Provider
      value={{
        isGraphView,
        isPredictionView,
        setIsGraphView,
        setIsPredictionView,
        chartData,
        taxReport,
        allEstateList,
        isLoadingEstate,
        selectedEstate,
        setSelectedEstate,
        selectedYear,
        setSelectedYear,
        minYear,
        maxYear,
        lastUpdatedRecurring,
        lastUpdatedOneTime,
        activeChart,
        setActiveChart,
        chartConfig: localizedChartConfig,
        total,
        analysis,
        portfolioEstimatedValue,
        transactions,
        yearTransactions,
        monthTransactions,
        income,
        expense,
        profit,
        selectedMonth,
        setSelectedMonth,
        totalsUpToMonth,
        lastYearProfit,
        currentYear,
        currentMonth,
        formDataMyEstate,
        setFormDataMyEstate,
        handleDiscardChanges,
        resetDetailSearch,
        saveMyEstate,
        openEditModalTransactions,
        openEditModal,
        isLoadingAnalysis,
        childrenEstateOffers,
        childChartDataBeforeReco,
        childChartDataAfterReco,
        childChartDataSellPrice,
        deleteEstate,
        totalProfit,
        defaultTransaction,
        transactionPreset,
        handleSubmitTransaction,
        verifyTransactionPeriods,
        sendTransactionReminder,
        defaultEstate,
        estateStats,
        isTaxView,
        setIsTaxView,
      }}
    >
      {children}
    </EstateContext.Provider>
  );
};

export const useEstateContext = () => useContext(EstateContext);
